import React from "react";
import { IconPlusFill } from "../buttons/IconButtons";
import CustomCard from "../CustomCard";
import NotInformation from "../NotInformation";
import SummaryItem from "./SummaryItem";

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azpata@ibl.mx
 *
 * Generic summary list card: titled header + two-column split of the selected
 * items (4 left / 3 right) with an overflow "+N" tile. Generalizes the
 * per-module containers (cafeterias, devices, departments, custom access) by
 * parametrizing the differing bits via props.
 *
 * @param {Object} props
 * @param {React.ComponentType} props.headerIcon - Card header icon.
 * @param {string} props.title - Card header title (already translated).
 * @param {string} props.emptyText - NotInformation text when nothing selected.
 * @param {React.ComponentType} props.avatarIcon - Icon inside the avatar box.
 * @param {Array} [props.selected=[]] - Items to render.
 * @param {Function} props.t - Translation function.
 * @param {Function} props.getLabel - (item) => string label/translation key.
 * @param {Function} [props.getKey] - (item, index) => React key. Defaults to item.id || index.
 * @param {Function} [props.renderValue] - (item) => node for the value column. When
 *   omitted the row renders label-only (twoColumns=false).
 * @param {boolean} [props.avatarAlwaysVisible=false] - When true the avatar column
 *   stays visible on mobile (custom-access layout); otherwise hidden under md.
 * @param {string} [props.overflowLabel='total_added_users'] - Translation key for the
 *   overflow "+N" tile label; pass the section's own (departments, devices, ...).
 * @returns {JSX.Element}
 */
const SummaryListCard = ({
                             headerIcon: HeaderIcon,
                             title,
                             emptyText,
                             avatarIcon: AvatarIcon,
                             selected = [],
                             t,
                             getLabel,
                             getKey = (item, index) => item.id || index,
                             renderValue,
                             avatarAlwaysVisible = false,
                             overflowLabel = 'total_added_users',
                         }) => {

    const rightItems = selected.slice(0, 4);
    const leftItems = selected.slice(4, 7);
    const extraCount = selected.length - 7;

    const avatarColClass = avatarAlwaysVisible
        ? "maxw-5 col-1 p-0"
        : "maxw-5 col-1 p-0 d-none d-md-block";
    const innerColClass = avatarAlwaysVisible
        ? "col-11 row p-0"
        : "col-12 col-md-11 row p-0";
    const firstColClass = avatarAlwaysVisible
        ? "col-6 gap-12px d-flex flex-column"
        : "col-12 col-md-6 gap-12px d-flex flex-column";

    const renderRow = (item, index) => (
        <SummaryItem
            key={getKey(item, index)}
            label={getLabel(item)}
            value={renderValue ? renderValue(item) : undefined}
            twoColumns={!!renderValue}
            t={t}
            className={'d-flex justify-content-between'}
            classNameValue={'w-300px'}
        />
    );

    return (
        <CustomCard className="p-3" classes="card-border-8px" classContent="p-0 overflow-hidden">
            <div className="d-flex align-items-center p-3">
                <div><HeaderIcon /></div>
                <div className="ml-2">
                    <p className="font-color-general-950 ibl-font-w-600 fs-14px">{title}</p>
                </div>
            </div>
            <div className="border-bottom-shades-300" />
            {
                selected.length <= 0
                    ? <NotInformation text={emptyText} card={true} />
                    : <div className={"row p-12px"}>
                        <div className={avatarColClass}>
                            <div className='background-primary-100 p-8px border-radius-8px d-flex justify-content-center
                align-items-center w-44px h-44px'>
                                <AvatarIcon width={24} height={24} />
                            </div>
                        </div>
                        <div className={innerColClass}>
                            <div className={firstColClass}>
                                {rightItems.map(renderRow)}
                            </div>
                            <div className={"col-12 col-md-6 gap-12px d-flex flex-column"}>
                                {leftItems.map(renderRow)}

                                {extraCount > 0 && (
                                    <SummaryItem
                                        label={overflowLabel}
                                        className={'background-shades-100 justify-content-between'}
                                        classNameValue={'d-flex justify-content-end'}
                                        value={
                                            <div className={"col-12 d-flex justify-content-end align-items-baseline p-0 pr-3"}>
                                                <div className={"mr-8px"}><IconPlusFill /></div>
                                                <p className={"fs-12px "}>{extraCount}</p>
                                            </div>
                                        }
                                        t={t}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
            }

        </CustomCard>
    )

}

export default SummaryListCard
